import { useEditor, EditorContent } from "@tiptap/react";
import StarterKit from "@tiptap/starter-kit";
import { Markdown } from "@tiptap/markdown";
import { Table, TableRow, TableHeader, TableCell } from "@tiptap/extension-table";
import Placeholder from "@tiptap/extension-placeholder";
import { useCallback, useEffect, useMemo, useRef } from "react";
import { EditorToolbar } from "./EditorToolbar";
import { FileRefExtension } from "./FileRefExtension";
import { stripTrailingPartialRef } from "../ai/markdown";
import "./editor.css";

export { FileRefClickProvider } from "./FileRefExtension";
export type { FileRefClickData } from "./FileRefExtension";

const sharedExtensions = [
  StarterKit.configure({ heading: { levels: [1, 2, 3] } }),
  Markdown.configure({ markedOptions: { gfm: true } }),
  Table.configure({ resizable: false }),
  TableRow,
  TableHeader,
  TableCell,
  FileRefExtension,
];

export function AnswerEditor(props: {
  content: string;
  onChange: (markdown: string) => void;
  placeholder?: string;
}) {
  const lastEmitted = useRef(props.content);
  const onChangeRef = useRef(props.onChange);
  onChangeRef.current = props.onChange;

  const handleUpdate = useCallback(({ editor }: { editor: any }) => {
    const md = editor.getMarkdown();
    lastEmitted.current = md;
    onChangeRef.current(md);
  }, []);

  const editor = useEditor({
    extensions: [
      ...sharedExtensions,
      Placeholder.configure({
        placeholder: props.placeholder ?? "Start typing...",
      }),
    ],
    content: props.content,
    contentType: "markdown",
    onUpdate: handleUpdate,
    editorProps: {
      attributes: {
        class: "answer-editor-content",
      },
    },
  });

  useEffect(() => {
    if (!editor) return;
    if (props.content === lastEmitted.current) return;
    lastEmitted.current = props.content;
    editor.commands.setContent(props.content, {
      contentType: "markdown",
    });
  }, [props.content, editor]);

  return (
    <div className="answer-prose answer-editor">
      {editor && <EditorToolbar editor={editor} />}
      <EditorContent editor={editor} />
    </div>
  );
}

export function AnswerViewer(props: { content: string }) {
  const safeContent = useMemo(
    () => stripTrailingPartialRef(props.content),
    [props.content],
  );

  const editor = useEditor({
    extensions: sharedExtensions,
    content: safeContent,
    contentType: "markdown",
    editable: false,
  });

  const lastContent = useRef(safeContent);

  useEffect(() => {
    if (!editor) return;
    if (safeContent === lastContent.current) return;
    lastContent.current = safeContent;
    editor.commands.setContent(safeContent, { contentType: "markdown" });
  }, [safeContent, editor]);

  return (
    <div className="answer-prose">
      <EditorContent editor={editor} />
    </div>
  );
}
